import Loader from "components/loader";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import crossIcon from "../../assets/svg/crossBtn.svg";
import uploadIcon from "../../assets/svg/upload-icon.svg";
import {
  addVideo,
  getAllVideoCategory,
  getVideoById,
  getVideoData,
  updateVideos,
} from "../../axios/api";
import {
  extractVideoId,
  renderError,
  renderImagePreview,
} from "../../utils/utils";
import { toast } from "react-toastify";

const AddVideos = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location?.state;

  const [videoData, setVideoData] = useState<any>();
  const [categories, setCategories] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const iconInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      thumbnailImage: null,
      thumbnailImageUrl: "",
      url: "",
      category: "",
    },
    onSubmit: async (values) => {
      // console.log("values", values);
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("locale", "1");
        formData.append("title", values.title);
        formData.append("description", values.description);
        formData.append("url", values.url);
        // formData.append(
        //   "thumbnail_url",
        //   values.thumbnailImageUrl || values.thumbnailImage
        // );
        formData.append(
          "thumbnail_url",
          (values?.thumbnailImage && values.thumbnailImage) ||
            (values?.thumbnailImageUrl &&
              values.thumbnailImageUrl.replace(
                /(https?:\/\/)?(?:\d{1,3}\.){3}\d{1,3}/,
                ""
              )) ||
            ""
        );
        formData.append("category_id", values.category);
        formData.append("video_gallery_id", values.title);
        // console.log("Form Data:", formData);
        let responseData;
        if (id) {
          responseData = await updateVideos(formData, id);
          if (responseData?.data?.success) {
            toast.success("Video Updated Successfully");
            navigate("/admin/video-gallery");
          }
        } else {
          responseData = await addVideo(formData);
          if (responseData?.data?.success) {
            toast.success("Video Added Successfully");
            navigate("/admin/video-gallery");
          }
        }
        if (!responseData?.data?.success) {
          toast.error(responseData?.data?.message);
        }
      } catch (error) {
        console.error("API Error:", error);
        toast.error("Something Went Wrong" + error);
      } finally {
        setLoading(false);
      }
    },
    validationSchema: yup.object({
      title: yup.string().trim().required("Video Title is required"),
      description: yup.string().required("Video description is required"),
      url: yup.string().required("Video url is required"),
      category: yup.string().required("category is required"),
    }),
  });

  const checkImage = useMemo(
    () =>
      renderImagePreview(
        formik.values.thumbnailImage,
        formik.values.thumbnailImageUrl
      ),
    [formik.values.thumbnailImage, formik.values.thumbnailImageUrl]
  );

  // Trigger fileInput on click on div
  const handleDivClick = (ref: any) => {
    ref.current.click();
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

  const handleDrop = (e: any, fieldName: any) => {
    e.preventDefault();
    formik.setFieldValue(fieldName, e.dataTransfer.files[0]);
  };

  useEffect(() => {
    const fetchVideo = async (id: string) => {
      try {
        setLoading(true);
        const initiativeDataResp = await getVideoById(id);
        setVideoData(initiativeDataResp?.data?.data);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchVideo(id);
    }
  }, [id]);

  useEffect(() => {
    if (videoData) {
      formik.setValues({
        title: videoData.title,
        description: videoData.description,
        thumbnailImage: null,
        thumbnailImageUrl: videoData.thumbnail_url,
        url: videoData.url,
        category: videoData?.category_id.id,
      });
    }
  }, [videoData]);

  useEffect(() => {
    const fetchAllVideoCategory = async () => {
      try {
        setLoading(true);
        const CategoryDataResp = await getAllVideoCategory(1, 9999);
        formik.setValues((prevValues) => ({
          ...prevValues,
          category: CategoryDataResp?.data?.data?.video_category[0]?.id || "",
        }));
        setCategories(CategoryDataResp?.data?.data?.video_category);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllVideoCategory();
  }, []);

  const getVideoDetails = async (e: any) => {
    e.preventDefault();
    try {
      setLoading(true);
      const videoId = extractVideoId(formik.values.url);
      if (videoId) {
        const videoDetailsResp = await getVideoData(videoId);
        formik.setValues((prevValues) => ({
          ...prevValues,
          title: videoDetailsResp?.data?.data?.youtubeData?.title || "",
          description:
            videoDetailsResp?.data?.data?.youtubeData?.description || "",
          thumbnailImage: null,
          thumbnailImageUrl:
            videoDetailsResp?.data?.data?.youtubeData?.thumbnail_url?.url || "",
        }));
      } else {
        toast.error("Wrong Youtube URL");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-3 bg-[#FFFFFF] p-3">
      {loading ? (
        <Loader />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="my-5 flex w-11/12 flex-col">
            <div className="flex w-[100%] items-center justify-between gap-4">
              <div className="my-2 flex min-w-[50%] flex-col items-start justify-center gap-2">
                <label htmlFor="title" className="text-xl font-medium">
                  Video Title
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Video title here"
                  className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                />
                {formik.touched.title && formik.errors.title ? (
                  <div className="text-lg text-red-500">
                    {renderError(formik.errors.title)}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex w-[100%] items-center justify-between gap-4">
              <div className="my-2 flex min-w-[50%] flex-col items-start justify-center gap-2">
                <label htmlFor="category" className="text-xl font-medium">
                  Category
                </label>
                <select
                  name="category"
                  id="category"
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] px-2 text-xl"
                >
                  {categories.length &&
                    categories.length > 0 &&
                    categories.map((category: any, index: any) => (
                      <option
                        value={category?.id}
                        label={category?.title}
                        key={index}
                        className="text-xl font-medium"
                      />
                    ))}
                </select>
                {formik.touched.category && formik.errors.category ? (
                  <div className="text-lg text-red-500">
                    {renderError(formik.errors.category)}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="my-2 flex w-[61%] flex-col items-start justify-center gap-2">
              <label htmlFor="url" className="text-xl font-medium">
                Video URL
              </label>
              <div className="relative w-[100%]">
                <input
                  type="text"
                  name="url"
                  id="url"
                  value={formik.values.url}
                  onChange={(e) => {
                    const youtubeUrlPattern =
                      /^(https:\/\/www\.youtube\.com\/watch\?v=|https:\/\/youtu\.be\/)[\w-]{11}$/;

                    if (youtubeUrlPattern.test(e.target.value)) {
                      formik.setFieldValue("url", e.target.value);
                    } else {
                      toast.error("Please enter a valid YouTube Video URL.");
                    }
                  }}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Video URL here"
                  className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                />
                <button
                  onClick={(e) => getVideoDetails(e)}
                  className="absolute right-3 top-[15%] rounded bg-[#008BD2] px-2  text-xl font-bold text-white"
                >
                  Fetch
                </button>
              </div>
              {formik.touched.url && formik.errors.url ? (
                <div className="text-lg text-red-500">
                  {renderError(formik.errors.url)}
                </div>
              ) : null}
            </div>
            <div className="my-5 flex items-center justify-evenly gap-5">
              <div className="w-[35%]">
                <label htmlFor="description" className=" text-xl font-medium">
                  Video Description
                </label>
                <div className="relative my-3 mb-5 flex w-[100%] items-center justify-between">
                  <textarea
                    rows={6}
                    cols={50}
                    name="description"
                    id="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Video description here"
                    className=" w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                  />
                </div>
                {formik.touched.description && formik.errors.description ? (
                  <div className="text-lg text-red-500">
                    {renderError(formik.errors.description)}
                  </div>
                ) : null}
              </div>

              <div className="w-[35%]">
                <label
                  htmlFor="thumbnailImageUrl"
                  className="text-xl font-medium"
                >
                  Video Thumbnail
                </label>
                {/* Icon Image Upload */}
                <div className="relative my-3 mb-5 flex w-[100%] items-center justify-between">
                  <input
                    //className="absolute bottom-0 left-8 -z-10 w-20"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    required={
                      !formik.values.thumbnailImageUrl &&
                      !formik.values.thumbnailImage
                    }
                    name="thumbnailImageUrl"
                    id="thumbnailImageUrl"
                    ref={iconInputRef}
                    type="file"
                    accept=".jpg, .jpeg, .png, .gif"
                    onChange={(e) => {
                      formik.setFieldValue("thumbnailImage", e.target.files[0]);
                    }}
                  />
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, "thumbnailImage")}
                    onClick={() => handleDivClick(iconInputRef)}
                    //className="flex h-44 w-full items-center justify-center rounded border-4 border-dashed"
                    className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded border-4 border-dashed"
                  >
                    {formik.values.thumbnailImageUrl ||
                    formik.values.thumbnailImage ? (
                      <>
                        <div className="relative flex items-center p-2">
                          {(formik.values.thumbnailImage ||
                            formik.values.thumbnailImageUrl) && (
                            <img
                              className=""
                              alt="thumbnailImage"
                              src={checkImage}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Stop event propagation
                            formik.setFieldValue("thumbnailImage", null);
                            formik.setFieldValue("thumbnailImageUrl", "");
                            iconInputRef.current.value = null;
                          }}
                          className="absolute right-1 top-1 z-10"
                        >
                          <img src={crossIcon} alt="Cross Icon" />
                        </button>
                      </>
                    ) : (
                      <div
                        id="imageDiv"
                        className="flex flex-col items-center justify-center text-center font-medium hover:cursor-pointer"
                      >
                        <img
                          className="mb-2 w-1/4"
                          src={uploadIcon}
                          alt="Upload Icon"
                        />
                        <span>
                          <span className="text-blue-500">Click here </span>to
                          Upload
                        </span>
                        <span>Video Thumbnail</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <button
                name="submit"
                className="float-right my-5 w-24 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
                type="submit"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddVideos;
